// Centralized pricing configuration for all premium features
// This mirrors the backend config in /velvynote-backend/src/config/featurePricing.ts

export const FEATURE_PRICING = {
  memories: {
    freeLimit: 10,                    // Free memories for signed-in users
    // Extra slots: $2/slot at peg 1 gem ≈ $0.04 → 50 gems per slot
    slotPacks: [
      { slots: 1, credits: 50 },
      { slots: 10, credits: 500 },
      { slots: 100, credits: 5000 },
    ],
  },
  videoGeneration: {
    basicCost: 500,                   // Future: basic video (placeholder, scaled with economy)
  },
  velvypal: {
    dailyFreeMessages: 3,              // Free messages per day (reset at midnight UTC)
    // ~$0.30/msg at peg (8 gems ≈ $0.32); 3-pack slight discount
    messagePacks: [
      { messages: 1, gems: 8, label: '1 message' },
      { messages: 3, gems: 22, label: '3 messages (best value)' },
    ],
  },
  landmarkZones: {
    // Premium zone radius in km by rarity (larger radius = more coverage)
    radiusKm: {
      legendary: 1.0,
      epic: 0.75,
      rare: 0.5,
    },
    // Guaranteed landmark placement: rare $10 / epic $20 / legendary $50 at peg
    gemCost: {
      rare: 250,
      epic: 500,
      legendary: 1250,
    },
  },
  // Credit packages for reference (actual packages in creditsApi.js)
  // Used to calculate dollar equivalents
  creditPackages: [
    { credits: 100, dollars: 5 },     // $0.050 per credit
    { credits: 250, dollars: 10 },    // $0.040 per credit (best value)
    { credits: 600, dollars: 20 },    // $0.033 per credit
  ],
  // Use middle-tier rate for dollar estimates (fair average)
  dollarsPerCredit: 0.04,
};

/**
 * Get a slot pack by its slot count
 * @param {number} slots
 * @returns {{ slots: number, credits: number } | undefined}
 */
export function getSlotPackBySlots(slots) {
  return FEATURE_PRICING.memories.slotPacks.find(pack => pack.slots === slots);
}

/**
 * Validate that slots and credits match a valid pack
 * @param {number} slots
 * @param {number} credits
 * @returns {boolean}
 */
export function isValidSlotPack(slots, credits) {
  const pack = getSlotPackBySlots(slots);
  return pack !== undefined && pack.credits === credits;
}

/**
 * Convert credits to approximate dollar value
 * @param {number} credits
 * @returns {string} Formatted dollar string (e.g., "$0.80")
 */
export function creditsToDollars(credits) {
  const dollars = credits * FEATURE_PRICING.dollarsPerCredit;
  // Format nicely: show cents for small amounts, dollars for larger
  if (dollars < 1) {
    return `$${dollars.toFixed(2)}`;
  }
  // Remove trailing zeros for clean display
  return `$${parseFloat(dollars.toFixed(2))}`;
}

/**
 * Get a velvypal message pack by its message count
 * @param {number} messages
 * @returns {{ messages: number, gems: number, label: string } | undefined}
 */
export function getMessagePackByMessages(messages) {
  return FEATURE_PRICING.velvypal.messagePacks.find(pack => pack.messages === messages);
}

/**
 * Validate that messages and gems match a valid velvypal pack
 * @param {number} messages
 * @param {number} gems
 * @returns {boolean}
 */
export function isValidMessagePack(messages, gems) {
  const pack = getMessagePackByMessages(messages);
  return pack !== undefined && pack.gems === gems;
}

/**
 * Get landmark zone pricing by rarity
 * @param {string} rarity - 'legendary', 'epic', or 'rare'
 * @returns {{ radiusKm: number, gemCost: number } | undefined}
 */
export function getLandmarkZonePricing(rarity) {
  const radiusKm = FEATURE_PRICING.landmarkZones.radiusKm[rarity];
  const gemCost = FEATURE_PRICING.landmarkZones.gemCost[rarity];
  if (radiusKm === undefined || gemCost === undefined) return undefined;
  return { radiusKm, gemCost };
}

/**
 * Validate that a landmark zone gem cost matches the expected cost for a rarity
 * @param {string} rarity
 * @param {number} gems
 * @returns {boolean}
 */
export function isValidLandmarkZoneCost(rarity, gems) {
  const pricing = getLandmarkZonePricing(rarity);
  return pricing !== undefined && pricing.gemCost === gems;
}
